import React, { useEffect, useRef, useState } from 'react';
import { SunIcon, MoonIcon, MenuIcon, LogOutIcon, SettingsIcon, AdminIcon, Loader2Icon, XIcon, MoreVerticalIcon, SearchIcon, PlusIcon, ChevronDownIcon, UploadIcon, KindGlyph, ChartIcon, KeyboardIcon } from '../constants';
import type { Theme } from '../App';
import type { ItemKind } from '../types';
import { ITEM_KINDS, KIND_META } from '../utils/itemKinds';

interface HeaderProps {
  theme: Theme;
  onToggleTheme: () => void;
  onLogout: () => void;
  onOpenAccountSettings: () => void;
  onOpenAdminDashboard: () => void;
  onOpenStats: () => void;
  onOpenShortcuts?: () => void;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  isMobile: boolean;
  syncStatus: string;
  isAdmin?: boolean;
  userEmail?: string;
  searchTerm: string;
  onSearch: (term: string) => void;
  onNew: (kind: ItemKind) => void;
  onImportSkill: () => void;
}

/** Wortmarke: drei Linien in den Farben der drei Eintragsarten */
export const BrandMark: React.FC<{ className?: string }> = ({ className = 'h-7 w-7' }) => (
  <svg className={className} viewBox="0 0 32 32" aria-hidden="true">
    <rect width="32" height="32" rx="7" fill="rgb(var(--ink))" />
    <path d="M8 9h16" stroke="rgb(var(--kind-prompt))" strokeWidth="3" strokeLinecap="round" />
    <path d="M8 16h11" stroke="rgb(var(--kind-skill))" strokeWidth="3" strokeLinecap="round" />
    <path d="M8 23h14" stroke="rgb(var(--kind-workflow))" strokeWidth="3" strokeLinecap="round" />
    <circle cx="22.5" cy="16" r="2.6" fill="none" stroke="rgb(var(--kind-skill))" strokeWidth="2" />
  </svg>
);

const useDismiss = (open: boolean, close: () => void, ref: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, ref]);
};

const SyncIndicator: React.FC<{ status: string }> = ({ status }) => {
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  useEffect(() => {
    if (status === 'idle') setLastSavedAt(new Date());
  }, [status]);

  const time = lastSavedAt?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const config: Record<string, { dot: string; label: string; title: string }> = {
    saving: { dot: 'bg-kind-prompt', label: 'Speichert …', title: 'Änderungen werden gespeichert' },
    offline: { dot: 'bg-amber-500', label: 'Offline', title: 'Keine Verbindung. Änderungen bleiben lokal und werden später übertragen.' },
    unauthorized: { dot: 'bg-red-500', label: 'Abgemeldet', title: 'Sitzung abgelaufen. Bitte neu anmelden.' },
    idle: { dot: 'bg-kind-skill', label: time ? `Gespeichert ${time}` : 'Gespeichert', title: time ? `Zuletzt gespeichert um ${time} Uhr` : 'Alles gespeichert' },
  };
  const c = config[status] ?? config.idle;

  return (
    <div className="flex items-center gap-2 px-2 text-xs text-graphite whitespace-nowrap" role="status" aria-live="polite" title={c.title}>
      {status === 'saving' ? (
        <Loader2Icon className="h-3.5 w-3.5 animate-spin text-kind-prompt" />
      ) : (
        <span className={`h-2 w-2 rounded-full ${c.dot}`} aria-hidden="true" />
      )}
      <span className="hidden xl:inline font-mono text-[11px]">{c.label}</span>
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({
  theme,
  onToggleTheme,
  onLogout,
  onOpenAccountSettings,
  onOpenAdminDashboard,
  onOpenStats,
  onOpenShortcuts,
  isSidebarOpen,
  toggleSidebar,
  isMobile,
  syncStatus,
  isAdmin,
  userEmail,
  searchTerm,
  onSearch,
  onNew,
  onImportSkill,
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const newRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useDismiss(isMoreOpen, () => setIsMoreOpen(false), moreRef);
  useDismiss(isNewOpen, () => setIsNewOpen(false), newRef);

  useEffect(() => {
    setIsMoreOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (isSearchOpen) searchRef.current?.focus();
  }, [isSearchOpen]);

  const userInitials = userEmail ? userEmail.split('@')[0].slice(0, 2).toUpperCase() : 'U';
  const menuItem = 'w-full flex items-center gap-3 px-3 py-2 text-sm text-ink hover:bg-ink/[0.06] text-left';

  const searchField = (
    <div className="relative w-full">
      <span className="absolute inset-y-0 left-3 flex items-center text-graphite pointer-events-none">
        <SearchIcon className="h-4 w-4" />
      </span>
      <input
        ref={searchRef}
        type="search"
        value={searchTerm}
        onChange={(e) => onSearch(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onSearch('');
            (e.target as HTMLInputElement).blur();
            setIsSearchOpen(false);
          }
        }}
        onBlur={() => { if (!searchTerm) setIsSearchOpen(false); }}
        placeholder="Bibliothek durchsuchen"
        data-search-input
        aria-label="Bibliothek durchsuchen"
        className={`w-full h-9 pl-9 ${searchTerm ? 'pr-9' : 'pr-3 xl:pr-20'} rounded-lg bg-paper border border-transparent text-sm text-ink placeholder:text-graphite/80 focus:bg-raised focus:border-kind-prompt focus:outline-none focus:ring-[3px] focus:ring-kind-prompt/15 transition-colors [&::-webkit-search-cancel-button]:hidden`}
      />
      <span className="absolute inset-y-0 right-2 flex items-center gap-1">
        {searchTerm ? (
          <button type="button" onClick={() => { onSearch(''); searchRef.current?.focus(); }} className="icon-btn !h-6 !w-6" aria-label="Suche leeren">
            <XIcon className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="hidden xl:inline-flex gap-1" aria-hidden="true"><kbd className="kbd">Strg</kbd><kbd className="kbd">K</kbd></span>
        )}
      </span>
    </div>
  );

  if (isMobile && isSearchOpen) {
    return (
      <header className="header sticky top-0 z-30 gap-2">
        {searchField}
        <button type="button" onClick={() => { onSearch(''); setIsSearchOpen(false); }} className="btn btn-ghost btn-sm shrink-0">Fertig</button>
      </header>
    );
  }

  return (
    <header className="header sticky top-0 z-30 gap-3">
      {/* Links: Menü und Marke */}
      <div className="flex items-center gap-2 shrink-0">
        {isMobile && (
          <button onClick={toggleSidebar} className="icon-btn" aria-label="Navigation öffnen" aria-expanded={isSidebarOpen}>
            <MenuIcon className="h-5 w-5" />
          </button>
        )}
        {isMobile && <BrandMark className="h-7 w-7" />}
      </div>

      {/* Mitte: Suche */}
      {!isMobile && <div className="flex-1 max-w-xl">{searchField}</div>}

      {/* Rechts */}
      <div className="flex items-center gap-1 shrink-0 ml-auto">
        {isMobile && (
          <button onClick={() => setIsSearchOpen(true)} className="icon-btn" aria-label="Suchen">
            <SearchIcon className="h-[18px] w-[18px]" />
          </button>
        )}

        <SyncIndicator status={syncStatus} />

        {/* Neu: geteilter Button, Hauptfläche legt einen Prompt an */}
        <div className="relative flex" ref={newRef}>
          <button
            type="button"
            onClick={() => onNew('prompt')}
            className="btn btn-primary btn-sm !rounded-r-none !pr-2.5"
            title="Neuer Prompt (Strg+N)"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Neu</span>
          </button>
          <button
            type="button"
            onClick={() => setIsNewOpen(o => !o)}
            className="btn btn-primary btn-sm !rounded-l-none !px-1.5 border-l !border-l-sheet/25"
            aria-haspopup="menu"
            aria-expanded={isNewOpen}
            aria-label="Art des neuen Eintrags wählen"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
          {isNewOpen && (
            <div className="context-menu absolute right-0 top-full mt-1.5 w-72 animate-scale-fade-in" role="menu">
              <div className="menu-label">Neu anlegen</div>
              {ITEM_KINDS.map(kind => (
                <button
                  key={kind}
                  role="menuitem"
                  className="w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-ink/[0.06]"
                  onClick={() => { onNew(kind); setIsNewOpen(false); }}
                >
                  <span className={`mt-0.5 ${KIND_META[kind].text}`}><KindGlyph kind={kind} /></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink">{KIND_META[kind].label}</span>
                    <span className="block text-xs text-graphite">{KIND_META[kind].hint}</span>
                  </span>
                </button>
              ))}
              <div className="border-t border-rule my-1" role="separator" />
              <button role="menuitem" className={menuItem} onClick={() => { onImportSkill(); setIsNewOpen(false); }}>
                <UploadIcon className="h-4 w-4 text-graphite" />
                SKILL.md importieren …
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-rule mx-1 hidden sm:block" aria-hidden="true" />

        {!isMobile && (
          <>
            <button onClick={onOpenStats} className="icon-btn" title="Statistik" aria-label="Statistik öffnen"><ChartIcon className="h-[18px] w-[18px]" /></button>
            {onOpenShortcuts && (
              <button onClick={onOpenShortcuts} className="icon-btn" title="Tastenkürzel (?)" aria-label="Tastenkürzel anzeigen"><KeyboardIcon className="h-[18px] w-[18px]" /></button>
            )}
            <button
              onClick={onToggleTheme}
              className="icon-btn"
              title={theme === 'light' ? 'Dunkles Design' : 'Helles Design'}
              aria-label={theme === 'light' ? 'Dunkles Design einschalten' : 'Helles Design einschalten'}
            >
              {theme === 'light' ? <MoonIcon className="h-[18px] w-[18px]" /> : <SunIcon className="h-[18px] w-[18px]" />}
            </button>
            <div className="w-px h-5 bg-rule mx-1" aria-hidden="true" />
          </>
        )}

        {/* Konto: Einstellungen, Verwaltung und Abmelden hinter dem Avatar, mit Beschriftung */}
        <div className="relative" ref={moreRef}>
          <button
            className={`flex items-center gap-1.5 h-9 pl-1 pr-1.5 rounded-full transition-colors hover:bg-ink/[0.07] ${isMoreOpen ? 'bg-ink/[0.07]' : ''}`}
            aria-label="Konto und Einstellungen"
            title={userEmail}
            aria-haspopup="menu"
            aria-expanded={isMoreOpen}
            onClick={() => setIsMoreOpen(o => !o)}
          >
            <span className="h-7 w-7 rounded-full bg-ink text-sheet flex items-center justify-center font-mono text-[10.5px] font-semibold" aria-hidden="true">
              {userInitials}
            </span>
            {isMobile ? <MoreVerticalIcon className="h-4 w-4 text-graphite" /> : <ChevronDownIcon className={`h-3.5 w-3.5 text-graphite transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} />}
          </button>
          {isMoreOpen && (
            <div className="context-menu absolute right-0 top-full mt-1.5 w-72 animate-scale-fade-in" role="menu">
              {userEmail && (
                <div className="px-3 pt-2 pb-2.5 border-b border-rule mb-1">
                  <div className="menu-label !p-0 !pb-0.5">Angemeldet als</div>
                  <div className="text-sm text-ink truncate">{userEmail}</div>
                </div>
              )}
              {isMobile && (
                <>
                  <button role="menuitem" className={menuItem} onClick={() => { onToggleTheme(); setIsMoreOpen(false); }}>
                    {theme === 'light' ? <MoonIcon className="h-4 w-4 text-graphite" /> : <SunIcon className="h-4 w-4 text-graphite" />}
                    {theme === 'light' ? 'Dunkles Design' : 'Helles Design'}
                  </button>
                  <button role="menuitem" className={menuItem} onClick={() => { onOpenStats(); setIsMoreOpen(false); }}>
                    <ChartIcon className="h-4 w-4 text-graphite" /> Statistik
                  </button>
                  <div className="border-t border-rule my-1" role="separator" />
                </>
              )}
              <button role="menuitem" className={menuItem} onClick={() => { onOpenAccountSettings(); setIsMoreOpen(false); }}>
                <SettingsIcon className="h-4 w-4 text-graphite" />
                <span className="flex-1">Einstellungen</span>
                <span className="text-xs text-graphite whitespace-nowrap">Konto, KI-Anbieter</span>
              </button>
              {isAdmin && (
                <button role="menuitem" className={menuItem} onClick={() => { onOpenAdminDashboard(); setIsMoreOpen(false); }}>
                  <AdminIcon className="h-4 w-4 text-graphite" />
                  <span className="flex-1">Verwaltung</span>
                  <span className="text-xs text-graphite">Benutzer</span>
                </button>
              )}
              <div className="border-t border-rule my-1" role="separator" />
              <button role="menuitem" className={`${menuItem} !text-red-700 dark:!text-red-400`} onClick={() => { onLogout(); setIsMoreOpen(false); }}>
                <LogOutIcon className="h-4 w-4" /> Abmelden
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
